/**
 * Formatter / parser compartidos para `<InputNumber>` de montos en pesos
 * chilenos (CLP).
 *
 * RESUELVEN DOS COSAS A LA VEZ:
 *
 * 1. El "0 líder" molesto. En un `<InputNumber>` PELADO (sin `parser`) AntD
 *    conserva el texto crudo mientras tipeas: si el campo trae `0` y escribes
 *    `5000`, se ve `05000` hasta que sales del campo (recién ahí re-formatea a
 *    `5000`). Con un `parser` que convierte a número, AntD re-formatea EN VIVO
 *    y el `0` de adelante desaparece apenas tipeas — que es lo lógico.
 *
 * 2. Legibilidad: agrupa los miles con punto (estilo es-CL) y antepone `$ `.
 *    Así un precio grande se lee `$ 1.250.000` en vez de `$ 1250000`.
 *
 * USO (reemplaza al `prefix="$"`, que ya no hace falta — el formatter agrega
 * el `$`):
 *
 *   import { clpInputFormatter, clpInputParser } from "../../utils/clpInput";
 *   <InputNumber
 *     min={0}
 *     formatter={clpInputFormatter}
 *     parser={clpInputParser as any}
 *   />
 *
 * Nota: el `parser` devuelve `0` para una entrada vacía (CLP entero). No usar
 * en filtros que necesiten distinguir "vacío" de `0` (esos manejan `null`
 * aparte) — solo en campos de monto donde `0` es el vacío natural.
 */

/**
 * Convierte un valor a la cadena visible `"$ 1.250.000"`. Limpia no-dígitos y
 * ceros a la izquierda antes de agrupar miles, así nunca se muestra `05000`.
 * Devuelve cadena vacía si no hay dígitos (deja ver el placeholder).
 */
export const clpInputFormatter = (
  value: string | number | undefined | null,
): string => {
  if (value === undefined || value === null || value === "") return "";
  const digits = String(value)
    .replace(/\D/g, "") // solo dígitos (descarta "$", puntos, espacios)
    .replace(/^0+(?=\d)/, ""); // quita ceros a la izquierda (deja un "0" solo)
  if (digits === "") return "";
  return `$ ${digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
};

/**
 * Extrae el entero CLP desde el texto mostrado (`"$ 1.250.000"` → `1250000`).
 * Entrada vacía → `0`.
 */
export const clpInputParser = (display: string | undefined): number => {
  const digits = String(display ?? "").replace(/\D/g, "");
  return digits ? Number(digits) : 0;
};
